import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Modal from '../UI/Modal';
import Button from '../UI/Button';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import withdrawalService, { UserSearchResult } from '../../api/services/withdrawal.service';

interface AddWithdrawalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddWithdrawalModal: React.FC<AddWithdrawalModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [amount, setAmount] = useState<number | ''>('');
  const [bankShortCode, setBankShortCode] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setDebounced('');
      setSelectedUser(null);
      setAmount('');
      setBankShortCode('');
      setBankName('');
      setAccountNumber('');
      setAccountName('');
      setNote('');
      setError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data: results, isFetching } = useQuery({
    queryKey: ['withdrawal-user-search', debounced],
    queryFn: () => withdrawalService.searchUsers(debounced),
    enabled: isOpen && !!debounced && !selectedUser,
    staleTime: 30 * 1000,
  });

  const handleSubmit = async () => {
    setError(null);
    if (!selectedUser) return setError('Please select a user');
    if (amount === '' || Number(amount) <= 0) return setError('Amount must be greater than 0');
    if (!bankShortCode.trim()) return setError('Bank short code is required');
    if (!bankName.trim()) return setError('Bank name is required');
    if (!accountNumber.trim()) return setError('Account number is required');
    if (!accountName.trim()) return setError('Account name is required');

    setSaving(true);
    try {
      await withdrawalService.create({
        userId: selectedUser.id,
        amount: Number(amount),
        bankShortCode: bankShortCode.trim(),
        bankName: bankName.trim(),
        accountNumber: accountNumber.trim(),
        accountName: accountName.trim(),
        note: note.trim() || undefined,
      });
      onSuccess();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to create withdrawal');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Withdrawal Request" size="md">
      <div className="space-y-4">
        {error && (
          <div className="text-xs bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        {/* User */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">User</label>
          {selectedUser ? (
            <div className="flex items-center justify-between bg-primary-50 border border-primary-200 rounded-lg px-3 py-2">
              <div>
                <p className="text-sm font-medium text-gray-900">{selectedUser.fullName}</p>
                <p className="text-[10px] text-gray-500">
                  @{selectedUser.username || '—'} · {selectedUser.telegramId} · Bal: {selectedUser.balance.toLocaleString()}
                </p>
              </div>
              <button
                onClick={() => setSelectedUser(null)}
                className="text-xs text-primary-700 hover:underline"
              >
                Change
              </button>
            </div>
          ) : (
            <>
              <div className="relative">
                <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, username, telegram id..."
                  className="w-full text-sm border border-gray-200 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              {debounced && (
                <div className="mt-2 max-h-40 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-50">
                  {isFetching && (
                    <div className="p-3 text-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent mx-auto" />
                    </div>
                  )}
                  {!isFetching && (results?.length || 0) === 0 && (
                    <p className="p-3 text-xs text-gray-500 text-center">No users found</p>
                  )}
                  {!isFetching &&
                    results?.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => setSelectedUser(u)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50"
                      >
                        <p className="text-sm font-medium text-gray-900">{u.fullName}</p>
                        <p className="text-[10px] text-gray-500">
                          @{u.username || '—'} · {u.telegramId} · Bal: {u.balance.toLocaleString()}
                        </p>
                      </button>
                    ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Amount */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Amount (ETB)</label>
          <input
            type="number"
            min={1}
            value={amount}
            onChange={(e) => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>

        {/* Bank info */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Bank Short Code</label>
            <input
              type="text"
              value={bankShortCode}
              onChange={(e) => setBankShortCode(e.target.value)}
              placeholder="CBE, AWASH, ..."
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Bank Name</label>
            <input
              type="text"
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder="Commercial Bank of Ethiopia"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Account Number</label>
            <input
              type="text"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Account Name</label>
            <input
              type="text"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Note (optional)</label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Creating...' : 'Create Withdrawal'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddWithdrawalModal;